import React, { useMemo, useState } from "react";
import "../styles/roman-conquest-britannia.css";

const SECTIONS = [
  {
    id: "overviews",
    title: "High-quality overviews (reliable starting points)",
    intro:
      "Read these first to get the basic narrative, chronology, and key actors before diving into disputes and details.",
    items: [
      {
        title: "English Heritage — The Roman invasion of Britain",
        type: "overview",
        tags: ["overview", "museum"],
        desc:
          "A clean narrative of the early campaigns (AD 43 onward) with a public-facing heritage perspective.",
        bestFor: "Grounding the story fast",
        useWhen: "Building a reader-friendly summary",
        href: "https://www.english-heritage.org.uk/learn/story-of-england/romans/invasion/",
      },
      {
        title: "Encyclopaedia Britannica — Roman Britain",
        type: "overview",
        tags: ["overview", "academic"],
        desc:
          "Encyclopaedic framing with strong editorial standards; useful for checking broad claims and context.",
        bestFor: "Quick verification and broad structure",
        useWhen: "You want a stable background framing",
        href: "https://www.britannica.com/place/Roman-Britain",
      },
      {
        title: "University of Warwick — The Roman Invasions of Britain",
        type: "overview",
        tags: ["overview", "museum"],
        desc:
          "University learning resource with an accessible summary of major events and links outward for deeper reading.",
        bestFor: "Teaching/learning structure",
        useWhen: "You want a quick ladder into sources",
        href: "https://warwick.ac.uk/fac/arts/classics/warwickclassicsnetwork/romancoventry/resources/interactions/invasion/",
      },
      {
        title: "Battlefields Trust — Celtic and Roman",
        type: "overview",
        tags: ["overview", "battle"],
        desc:
          "A battle-led gateway into the era, ideal if you’re approaching the conquest through conflict sites and military episodes.",
        bestFor: "Battle-led orientation",
        useWhen: "Building a battles/operations reading path",
        href: "https://www.battlefieldstrust.com/resource-centre/roman/",
      },
    ],
  },
  {
    id: "maps",
    title: "Map/atlas style resources (visualise the campaigns)",
    intro:
      "Use these when you want geography to do the storytelling: landings, routes, river crossings, tribal zones, and frontiers.",
    items: [
      {
        title: "OmniAtlas — Claudius’ invasion of Britain (43)",
        type: "map",
        tags: ["map", "overview"],
        desc:
          "Interactive atlas presentation with a concise narrative panel alongside the map.",
        bestFor: "Spatial overview",
        useWhen: "Explaining landing → advance → capture sequence",
        href: "https://omniatlas.com/maps/europe/430816/",
      },
      {
        title: "OmniAtlas — Roman Conquest of Britain (47)",
        type: "map",
        tags: ["map"],
        desc:
          "A follow-on atlas map for the broader consolidation phase (handy for “after the first shock”).",
        bestFor: "Expansion/consolidation view",
        useWhen: "Mapping the widening grip across regions",
        href: "https://omniatlas.com/maps/europe/470430/",
      },
      {
        title: "World History Encyclopedia — Map of the British Isles in AD 43",
        type: "map",
        tags: ["map", "overview"],
        desc:
          "A clear snapshot map to help readers anchor tribal and regional geography at the moment the conquest begins.",
        bestFor: "‘Who is where?’ background",
        useWhen: "Framing the political landscape",
        href: "https://www.worldhistory.org/image/742/map-of-the-british-isles-in-ad-43/",
      },
    ],
  },
  {
    id: "battles",
    title: "Campaign detail and battle-specific mapping",
    intro:
      "Ideal when your reader wants to zoom in: a single clash, a river crossing, a tactical sequence.",
    items: [
      {
        title: "BritishBattles — Battle of the Medway (43)",
        type: "battle",
        tags: ["battle", "map", "specialist"],
        desc:
          "Battle narrative with supporting diagrams/maps — excellent for a ‘battlefield briefing’ feel.",
        bestFor: "Medway battle walk-through",
        useWhen: "You want maps + readable sequence",
        href: "https://www.britishbattles.com/wars-of-roman-britain/battle-of-medway/",
      },
      {
        title: "RomanoBritain — The Roman Invasion of Britain (43 AD)",
        type: "battle",
        tags: ["battle", "overview", "specialist"],
        desc:
          "Structured overview including invasion planning, early actions, and Medway within the wider campaign.",
        bestFor: "Chaptered campaign narrative",
        useWhen: "Guided reading path for newcomers",
        href: "https://www.romanobritain.org/6-history/his_roman_invasion.php",
      },
      {
        title: "BattleMaps.eu — Battle of the Medway (animated map)",
        type: "map",
        tags: ["map", "battle"],
        desc:
          "Animated-map style view — useful for visitors who think in movement rather than paragraphs.",
        bestFor: "Visual learners",
        useWhen: "You want movement on a map",
        href: "https://www.battlemaps.eu/battles/medway.html",
      },
    ],
  },
  {
    id: "specialists",
    title: "Specialist Roman-Britain history sites (detailed reading)",
    intro:
      "Deeper compendia: handy for chronology, named figures, and stitched summaries that point toward classical sources.",
    items: [
      {
        title: "Roman Britain — The Claudian Invasion of Britain (43AD)",
        type: "specialist",
        tags: ["specialist", "overview"],
        desc:
          "Detailed compilation focused on the invasion phase, with attention to motivations, actors, and sequence.",
        bestFor: "Checking names/events quickly",
        useWhen: "Drafting timeline paragraphs",
        href: "https://www.roman-britain.co.uk/roman-conquest-and-occupation-of-britain/initial-claudian-invasion-and-conquest-43-60-ad/the-claudian-invasion-of-britain/",
      },
      {
        title: "Roman Britain — Claudian Invasion and Conquest (43–60AD)",
        type: "specialist",
        tags: ["specialist"],
        desc:
          "Broader consolidation phase resource — useful beyond the first landing and early victories.",
        bestFor: "Linking invasion into occupation",
        useWhen: "Guiding deeper research journeys",
        href: "https://www.roman-britain.co.uk/roman-conquest-and-occupation-of-britain/initial-claudian-invasion-and-conquest-43-60-ad/",
      },
      {
        title: "Roman Britain — Why did Claudius invade Britain?",
        type: "specialist",
        tags: ["specialist"],
        desc:
          "Focused ‘why’ page — perfect as a mini-essay prompt on motives and imperial politics.",
        bestFor: "Motive debates",
        useWhen: "Inviting competing explanations",
        href: "https://www.roman-britain.co.uk/roman-conquest-and-occupation-of-britain/initial-claudian-invasion-and-conquest-43-60-ad/why-did-claudius-invade-britain/",
      },
    ],
  },
  {
    id: "academic",
    title: "Academic scholarship (peer-reviewed / research-led)",
    intro:
      "For the ‘how do we know?’ reader: sources, reliability, and interpretation (some may be paywalled).",
    items: [
      {
        title: "Cambridge Core — Frere (2001), “The Roman Invasion of A.D. 43”",
        type: "academic",
        tags: ["academic"],
        desc:
          "Key article in Britannia examining the invasion narrative and the reliability of surviving accounts.",
        bestFor: "Source criticism and reconstruction",
        useWhen: "You want academic grounding",
        href: "https://www.cambridge.org/core/journals/britannia/article/roman-invasion-of-ad-43/4668E253345EAEDBE003E90F09FB19C9",
      },
    ],
  },
];

const FILTERS = [
  { key: "overview", label: "Overview" },
  { key: "map", label: "Map / Atlas" },
  { key: "battle", label: "Battle Focus" },
  { key: "specialist", label: "Specialist Site" },
  { key: "academic", label: "Academic" },
];

export default function ConquestOfBritannia() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();

  const filteredSections = useMemo(() => {
    const matchesFilter = (item) =>
      activeFilter === "all" ? true : item.tags.includes(activeFilter);

    const matchesQuery = (item) => {
      if (!q) return true;
      const hay = `${item.title} ${item.desc} ${item.bestFor} ${item.useWhen}`.toLowerCase();
      return hay.includes(q);
    };

    return SECTIONS.map((sec) => {
      const items = sec.items.filter((it) => matchesFilter(it) && matchesQuery(it));
      return { ...sec, items };
    }).filter((sec) => sec.items.length > 0);
  }, [activeFilter, q]);

  return (
    <div className="conquest-wrapper">
      <header className="conquest-header">
        <h1 className="conquest-title">Roman Conquest of Britannia — Guided Resources</h1>
        <p className="conquest-subtitle">
          A curated gateway into the conquest: reliable overviews, campaign maps, battle-specific material,
          specialist syntheses, and academic scholarship. Use the filters to explore.
        </p>
      </header>

      <section className="conquest-filter-bar" aria-label="Resource filters">
        <button
          className={`conquest-filter-all ${activeFilter === "all" ? "active" : ""}`}
          type="button"
          onClick={() => setActiveFilter("all")}
        >
          All resources
        </button>

        <div className="conquest-filter-chips" role="group" aria-label="Filter by type">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`conquest-filter-chip ${activeFilter === f.key ? "active" : ""}`}
              type="button"
              onClick={() => setActiveFilter(f.key)}
            >
              <span className="conquest-filter-dot" />
              {f.label}
            </button>
          ))}
        </div>

        <div className="conquest-search">
          <label className="conquest-search-label" htmlFor="searchInput">Search</label>
          <input
            id="searchInput"
            className="conquest-search-input"
            type="search"
            placeholder="Try: Claudius, Medway, Cassius Dio, Caratacus, maps…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <button
            className="conquest-search-clear"
            type="button"
            onClick={() => setQuery("")}
          >
            Clear
          </button>
        </div>
      </section>

      <div className="conquest-layout">
        <main className="conquest-left" aria-label="Resource sections">
          {filteredSections.map((sec) => (
            <section className="conquest-section" key={sec.id} id={sec.id}>
              <div className="conquest-section-header">
                <h2>{sec.title}</h2>
                <p className="conquest-muted">{sec.intro}</p>
              </div>

              <div className="conquest-cards">
                {sec.items.map((it) => (
                  <article
                    key={it.href}
                    className="conquest-card"
                    data-tags={it.tags.join(" ")}
                  >
                    <div className="conquest-card-top">
                      <h3>{it.title}</h3>
                      <span className="conquest-badge">{it.type}</span>
                    </div>

                    <p className="conquest-card-desc">{it.desc}</p>

                    <ul className="conquest-card-meta">
                      <li><strong>Best for:</strong> {it.bestFor}</li>
                      <li><strong>Use when:</strong> {it.useWhen}</li>
                    </ul>

                    <a
                      className="conquest-card-link"
                      href={it.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open resource →
                    </a>
                  </article>
                ))}
              </div>
            </section>
          ))}

          <footer className="conquest-footer">
            <p className="conquest-muted">
              Tip: start with “High-quality overviews”, then open “Map/atlas” in another tab to follow the geography as you read.
              Academic items are ideal once you’ve formed questions you want tested.
            </p>
          </footer>
        </main>

        <aside className="conquest-right" aria-label="Quick paths">
          <div className="conquest-sticky">
            <h3 className="conquest-side-title">Quick paths</h3>

            <div className="conquest-side-card">
              <h4>Fast orientation (15 minutes)</h4>
              <ol>
                <li>English Heritage overview</li>
                <li>OmniAtlas 43 map</li>
                <li>World History AD 43 map</li>
              </ol>
            </div>

            <div className="conquest-side-card">
              <h4>Battle-led route</h4>
              <ol>
                <li>Battlefields Trust gateway</li>
                <li>Medway (BritishBattles)</li>
                <li>Medway animated map</li>
              </ol>
            </div>

            <div className="conquest-side-card">
              <h4>Source &amp; interpretation route</h4>
              <ol>
                <li>Britannica (overview)</li>
                <li>Frere (Cambridge Core)</li>
                <li>Roman Britain — Why did Claudius invade Britain?</li>
              </ol>
            </div>

            <div className="conquest-jumplist" aria-label="Jump to section">
              <h4>Jump to…</h4>
              <a href="#overviews" className="conquest-jump">Overviews</a>
              <a href="#maps" className="conquest-jump">Maps / Atlas</a>
              <a href="#battles" className="conquest-jump">Battles</a>
              <a href="#specialists" className="conquest-jump">Specialist Sites</a>
              <a href="#academic" className="conquest-jump">Academic</a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
